import { Component, DestroyRef, ElementRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ModalService, SiteService } from '@card-software/library';
import { DyehouseDailyMetricDto, DyehouseDailyMetricImportCommand } from '../../../models/production/dyehouse-daily-metric.model';
import { DyehouseDailyMetricProxyService } from '../../../proxies/production/dyehouse-daily-metric.proxy.service';
import { DyehouseDailyMetricPreviewModalComponent } from './_modals/dyehouse-daily-metric-preview-modal/dyehouse-daily-metric-preview-modal.component';

@Component({
  selector: 'app-dyehouse-daily-metric',
  templateUrl: './dyehouse-daily-metric.component.html',
  styleUrl: './dyehouse-daily-metric.component.scss',
})
export class DyehouseDailyMetricComponent implements OnInit {
  private readonly dyehouseDailyMetricProxyService = inject(DyehouseDailyMetricProxyService);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);
  private readonly modalService = inject(ModalService);
  private readonly destroyRef = inject(DestroyRef);

  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;

  isLoading = signal(0);
  lastRecords = signal<DyehouseDailyMetricDto[]>([]);

  file = new FormControl<File | null>(null, [Validators.required]);

  form = new FormGroup({
    file: this.file,
  });

  filterYear = new FormControl<number | null>(new Date().getFullYear());
  filterMonth = new FormControl<number | null>(new Date().getMonth() + 1);

  readonly yearOptions: { label: string; value: number }[] = (() => {
    const current = new Date().getFullYear();
    return [current - 2, current - 1, current, current + 1].map((y) => ({ label: String(y), value: y }));
  })();

  get monthOptions(): { label: string; value: number }[] {
    return Array.from({ length: 12 }, (_, i) => ({
      label: String(i + 1).padStart(2, '0'),
      value: i + 1,
    }));
  }

  ngOnInit(): void {
    this.loadRecords();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selected = input.files && input.files.length ? input.files[0] : null;

    if (!selected) {
      this.file.setValue(null);
      return;
    }

    if (!selected.name.toLowerCase().endsWith('.xlsx')) {
      this.file.setValue(null);
      if (this.fileInputRef?.nativeElement) {
        this.fileInputRef.nativeElement.value = '';
      }
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: this.translateService.instant('DyehouseDailyMetric.invalid_file_detail') || 'Only .xlsx files are allowed.',
        life: 3000,
      });
      return;
    }

    this.file.setValue(selected);
    this.file.markAsTouched();
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.startPreview(this.file.value!);
  }

  private startPreview(file: File): void {
    const previewCommand: DyehouseDailyMetricImportCommand = {
      file,
      plantId: SiteService.getSelectedSiteId() ?? '',
      preview: true,
      override: false,
    };

    this.startLoading();
    this.dyehouseDailyMetricProxyService
      .import(previewCommand)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          if (response?.success && response.data) {
            this.modalService
              .open(DyehouseDailyMetricPreviewModalComponent, { modalClass: 'modal_large' }, { previewResult: response.data })
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe((modalResult) => {
                if (modalResult.action === 'save') {
                  this.executeSave(file, modalResult.data?.override ?? false);
                } else {
                  this.loadRecords();
                }
              });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: this.translateService.instant('DyehouseDailyMetric.import_error') || 'Could not preview the file.',
              life: 3000,
            });
          }
        },
        error: (error) => {
          console.error('Error previewing dyehouse daily metric file:', error);
        },
      });
  }

  private executeSave(file: File, override: boolean): void {
    const saveCommand: DyehouseDailyMetricImportCommand = {
      file,
      plantId: SiteService.getSelectedSiteId() ?? '',
      preview: false,
      override,
    };

    this.startLoading();
    this.dyehouseDailyMetricProxyService
      .import(saveCommand)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          if (response?.success) {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: this.translateService.instant('DyehouseDailyMetric.import_success') || 'File imported successfully.',
              life: 3000,
            });
            this.resetForm();
            this.loadRecords();
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: this.translateService.instant('DyehouseDailyMetric.import_error') || 'Could not import the file.',
              life: 3000,
            });
          }
        },
        error: (error) => {
          console.error('Error saving dyehouse daily metric file:', error);
        },
      });
  }

  downloadTemplate(): void {
    window.open('https://sierra-erp-assets.s3.us-east-1.amazonaws.com/docs/Formato%20Consumos%20Diarios%20Mantenimiento.xlsx', '_blank');
  }

  onApplyFilter(): void {
    this.loadRecords();
  }

  onResetFilter(): void {
    this.filterYear.setValue(new Date().getFullYear());
    this.filterMonth.setValue(new Date().getMonth() + 1);
    this.loadRecords();
  }

  private loadRecords(): void {
    const year = this.filterYear.value ?? new Date().getFullYear();
    const month = this.filterMonth.value ?? new Date().getMonth() + 1;

    this.startLoading();
    this.dyehouseDailyMetricProxyService
      .getByPeriod(year, month)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          if (response?.success && response.data) {
            this.lastRecords.set(response.data);
          }
        },
        error: (err) => console.error('Error loading dyehouse daily metrics:', err),
      });
  }

  private resetForm(): void {
    this.form.reset();
    if (this.fileInputRef?.nativeElement) {
      this.fileInputRef.nativeElement.value = '';
    }
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }
}
